/**
 * Inspection Detail Page - beta foundation.
 *
 * Renders a single Inspection record with status banner, lifecycle
 * stepper, editable scheduling/contact/property cards, result + repair
 * sections (when complete), evidence list, and action buttons (cancel
 * works now; workflow transitions wire once the workflow seed lands).
 *
 * Composes shared Tier-1 components per the platform rule that
 * dedicated pages use the same shared editing primitives the Entity
 * Editor uses internally.
 */

import { useMemo, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, Link } from "react-router";
import {
  ArrowLeft,
  ClipboardCheck,
  ClipboardList,
  CalendarCheck,
  MapPin,
  FileText,
  ShieldAlert,
  CheckCircle2,
  Flag,
  XCircle,
  PlayCircle,
  Wrench,
  Loader2,
  Camera,
  Pencil,
  User,
  HardHat,
  CornerDownRight,
  Send,
  Download,
  Share2,
} from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/admin/components/app-shell";
import { Button } from "@/shared/components/ui/button";
import { type UploadedFileItem } from "@/shared/lib/file-types";
import { AddEvidenceDialogShell } from "@/shared/components/evidence/add-evidence-dialog-shell";
import {
  EvidenceFileList,
  type EvidenceFileListItem,
} from "@/shared/components/evidence/evidence-file-list";
import { AuthenticatedImage } from "@/admin/components/authenticated-image";
import {
  WorkflowActionMenu,
  WorkflowProgressRail,
  WorkflowStateBadge,
} from "@/shared/components/workflows";
import {
  useWorkflowInstance,
  useWorkflowHistory,
  useExecuteWorkflowAction,
  invalidateVisibleActions,
  invalidateWorkflowInstanceQueries,
} from "@/shared/hooks/use-workflows";
import { computeVisitedStateKeys } from "@/shared/lib/workflows/visited-states";
import type { WorkflowInstance } from "@/shared/lib/workflows/types";
import { Badge } from "@/shared/components/ui/badge";
import { Separator } from "@/shared/components/ui/separator";
import { Label } from "@/shared/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/shared/components/ui/tabs";
import { AssignmentPill } from "@/admin/components/shared/assignment-pill";
import { EditableDetailCard } from "@/admin/components/shared/editable-detail-card";
import { DetailCard } from "@/admin/components/shared/detail-card";
import { NotesPanel } from "@/admin/components/shared/notes-panel";
import { RecordForms } from "@/admin/components/forms/record-forms";
import { SendFormDialog } from "@/admin/components/forms/send-form-dialog";
import { useFormTemplates, useRecordForms } from "@/admin/hooks/use-forms";
import { useOrg } from "@/admin/hooks/use-org";
import { DraggableCardGrid } from "@/admin/components/shared/draggable-card-grid";
import { RecordCommunicationsCard } from "@/admin/components/composer/record-communications-card";
import { ShareReportDialog } from "./share-report-dialog";
import { PinMyViewBar } from "@/admin/components/shared/pin-my-view";
import { usePinnedView } from "@/admin/hooks/use-pinned-view";
import type { CardFieldItem } from "@/admin/components/shared/editable-card-types";
import { defineField } from "@/shared/lib/forms";
import { DatePicker } from "@/shared/components/ui/date-picker";
import { Input } from "@/shared/components/ui/input";
import { Textarea } from "@/shared/components/ui/textarea";
import { TimeSelect } from "@/shared/components/ui/time-select";
import { toastApiError } from "@/shared/lib/api-error";
import { type SearchableSelectGroup } from "@/shared/components/ui/searchable-select";
import {
  useInspection,
  useUpdateInspection,
  useConfirmAppointment,
  useMarkInspectionPriority,
  useAssignFollowUp,
  useAssignInspector,
  useInspectorSearch,
  useInspectionPunchList,
  useCreatePunchListItem,
  useClosePunchListWorkItem,
  useReviewPunchListItem,
  useActionOwnerLabel,
  usePunchListSendPreview,
  useSendPunchList,
  useUploadInspectionEvidence,
  useInspectionChoices,
  useInspectionChecklist,
  useDownloadInspectionReport,
  type InspectionDetail,
  type InspectionEvidence,
  type PunchListItem,
} from "@/admin/hooks/use-inspections";
import { useUploadFile } from "@/admin/hooks/use-products";
import { usePermissions } from "@/admin/hooks/use-permissions";
import { SEND_PERMISSION_BY_KIND, SEND_REQUESTS } from "@/admin/lib/permissions";
import { useResourceLookupProviders } from "@/admin/hooks/use-resource-lookup-providers";
import { ResourceLookupContextProvider } from "@/shared/components/ui/resource-lookup-context";
import { ClosePunchListItemDialog } from "./close-punch-list-item-dialog";
import { ProofLinksSection } from "./proof-links-section";
import { PunchListItemDialog } from "./punch-list-item-dialog";
import { type ProofDecision, ReviewProofDialog } from "./review-proof-dialog";
import {
  useMarkNotificationsRead,
  useNotificationItemAction,
  useNotifications,
} from "@/admin/hooks/use-notifications";
import { NotificationsPanel } from "@/admin/components/notifications-panel";
import { InspectionChecklistTab } from "./inspection-checklist-tab";
import { InspectionReportTab } from "./inspection-report-tab";
import { useFormatters } from "@/shared/lib/formatting";

// Icon-by-name allowlist: maps a workflow state key to a lucide icon for
// the status banner's leading glyph. This is a presentation allowlist
// only (icons-by-name is permitted per the build principles) - the state
// label, colour and terminal flag are NOT hardcoded here; they come from
// the engine's WorkflowState via WorkflowStateBadge.
const STATE_ICONS: Record<string, typeof CalendarCheck> = {
  draft: ClipboardCheck,
  scheduled: CalendarCheck,
  confirmed: CalendarCheck,
  risk_assessment: ClipboardCheck,
  in_progress: PlayCircle,
  passed: CheckCircle2,
  repair_pending: Wrench,
  repair_complete: CheckCircle2,
  failed: XCircle,
  not_completed: XCircle,
  closed: CheckCircle2,
  cancelled: XCircle,
};

function buildLabelMap(
  options: { value: string; label: string }[] | undefined,
): Record<string, string> {
  if (!options) return {};
  return Object.fromEntries(options.map((o) => [o.value, o.label]));
}

function StatusBanner({
  inspection,
  instance,
}: {
  inspection: InspectionDetail;
  instance: WorkflowInstance | null | undefined;
}) {
  const { data: choices } = useInspectionChoices();
  const { resultLabels, reasonLabels } = useMemo(
    () => ({
      resultLabels: buildLabelMap(choices?.results),
      reasonLabels: buildLabelMap(choices?.result_reasons),
    }),
    [choices],
  );
  const currentState = instance?.current_state;
  // Derive the states this inspection ACTUALLY visited from its action-log
  // history, so the rail marks a state ``done`` only when it was entered -
  // never by ordinal position (a shortcut past a state must render skipped).
  // Left undefined until the history query resolves so the rail keeps its
  // position-based fallback rather than flashing prior states as skipped.
  const { data: history, isPending: historyPending } = useWorkflowHistory(
    instance?.uuid,
  );
  const historyReady = Boolean(instance?.uuid) && !historyPending;
  const visitedStateKeys = useMemo(
    () =>
      historyReady
        ? computeVisitedStateKeys(history, {
            states: instance?.states,
            currentStateKey: currentState?.key ?? inspection.lifecycle_status,
          })
        : undefined,
    [
      historyReady,
      history,
      instance?.states,
      currentState?.key,
      inspection.lifecycle_status,
    ],
  );
  // The engine's current_state is authoritative for the label/colour; fall
  // back to the resource's lifecycle_status key only for the leading icon
  // while the instance loads.
  const stateKey = currentState?.key ?? inspection.lifecycle_status;
  const Icon = STATE_ICONS[stateKey] ?? ClipboardCheck;
  return (
    <div className="flex flex-col gap-4 rounded-lg border border-[var(--rule)] bg-[var(--bg-card)] p-4">
      <div className="flex items-center gap-3">
        <Icon size={20} className="text-[var(--ink-2)]" />
        <div className="flex flex-1 flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[12px] text-[var(--ink-3)]">
              {inspection.code}
            </span>
            <Separator orientation="vertical" className="h-4" />
            <span className="text-sm font-medium text-[var(--ink)]">
              {inspection.sub_type.label}
            </span>
          </div>
          {inspection.result && (
            <span className="text-xs text-[var(--ink-3)]">
              {resultLabels[inspection.result] ?? inspection.result}
              {inspection.result_reason
                ? ` (${reasonLabels[inspection.result_reason] ?? inspection.result_reason})`
                : ""}
            </span>
          )}
        </div>
        {currentState && (
          <WorkflowStateBadge
            stateKey={currentState.key}
            workflowKey="default_inspection_workflow"
            label={currentState.label}
            color={currentState.color}
            isTerminal={currentState.is_terminal}
          />
        )}
      </div>
      {/* Engine-driven progress rail: walks the instance's own state graph
          so it always reflects the current workflow definition and marks
          exactly where this inspection sits. */}
      {instance?.states && instance.states.length > 0 && (
        <>
          <Separator />
          <WorkflowProgressRail
            states={instance.states}
            transitions={instance.transitions}
            currentStateKey={currentState?.key ?? inspection.lifecycle_status}
            visitedStateKeys={visitedStateKeys}
            workflowKey="default_inspection_workflow"
          />
        </>
      )}
    </div>
  );
}

function EvidenceList({ items }: { items: InspectionEvidence[] }) {
  const { t } = useTranslation();
  const { formatDateTime } = useFormatters();

  const listItems: EvidenceFileListItem[] = items.map((ev) => {
    const captured =
      ev.gps_lat != null && ev.gps_lng != null
        ? `GPS ${Number(ev.gps_lat).toFixed(4)}, ${Number(ev.gps_lng).toFixed(4)}`
        : null;
    return {
      key: ev.uuid,
      fileName: ev.file?.file_name ?? ev.kind.replace(/_/g, " "),
      fileUuid: ev.file?.uuid,
      fileUrl: ev.file?.file_url,
      thumbnailUrl: ev.file?.thumbnail_url,
      thumbnail: ev.file?.uuid ? (
        <AuthenticatedImage
          src={ev.file.thumbnail_url ?? ev.file.file_url}
          alt={ev.file.file_name ?? ev.kind.replace(/_/g, " ")}
          className="h-full w-full object-cover"
          fallback={null}
        />
      ) : undefined,
      badges: (
        <>
          <Badge variant="secondary" className="capitalize">
            {ev.kind.replace(/_/g, " ")}
          </Badge>
          {ev.tagged_line_items?.map((tag) => (
            <Badge key={tag} variant="outline" className="capitalize">
              {tag.replace(/_/g, " ")}
            </Badge>
          ))}
          {ev.rejected_at ? (
            <Badge variant="destructive">
              {t("inspections.evidenceSentBack", "Sent back")}
            </Badge>
          ) : ev.approved_at ? (
            <Badge variant="outline">
              {t("inspections.evidenceApproved", "Approved")}
            </Badge>
          ) : null}
        </>
      ),
      meta: (
        <>
          {ev.caption ? <span>{ev.caption}</span> : null}
          <span>{formatDateTime(ev.captured_at ?? ev.created_at)}</span>
          {captured ? <span>{captured}</span> : null}
          {ev.uploaded_by_email ? <span>{ev.uploaded_by_email}</span> : null}
          {ev.rejection_reason ? (
            <span>
              {t("inspections.evidenceSentBackReason", "Sent back: {{reason}}", {
                reason: ev.rejection_reason,
              })}
            </span>
          ) : null}
        </>
      ),
    };
  });

  return (
    <EvidenceFileList
      items={listItems}
      emptyMessage="No evidence uploaded yet."
    />
  );
}

function PunchListItemRow({
  item,
  categoryLabel,
  ownerText,
  onEdit,
  onClose,
  onReview,
  canResolve,
}: {
  item: PunchListItem;
  categoryLabel: string;
  /** Who owns the action, by role and name. */
  ownerText: string;
  onEdit: () => void;
  /** Opens the dialog that resolves it, with proof, or waives it. */
  onClose: () => void;
  /** Opens the dialog that approves its proof or sends it back. */
  onReview: (decision: ProofDecision) => void;
  canResolve: boolean;
}) {
  const { t } = useTranslation();
  const { formatDate, formatDateTime } = useFormatters();
  const isOpen = item.status === "open";
  // Proof from a link waits for review; the action is still open. Proof that
  // was sent back no longer counts.
  const proofIn =
    isOpen &&
    (item.proof ?? []).some((proof) => !proof.rejected_at && !proof.approved_at);
  // ISO dates compare as strings; an open action past its date is overdue.
  const overdue =
    isOpen && !!item.due_date && item.due_date < new Date().toLocaleDateString("sv-SE");
  return (
    <div
      data-testid="punch-list-item"
      className={`flex items-start gap-3 rounded-lg border-l-[3px] border-y-[0.5px] border-r-[0.5px] ${
        isOpen
          ? "border-l-[var(--status-warn,#92400e)] border-y-[var(--rule)] border-r-[var(--rule)] bg-[var(--bg-card)]"
          : "border-l-[var(--status-ok,#1c5a48)] border-y-[var(--rule)] border-r-[var(--rule)] bg-[var(--bg-light)]"
      } p-3`}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center gap-2">
          <Badge
            variant={proofIn ? "secondary" : isOpen ? "destructive" : "outline"}
          >
            {proofIn
              ? t("inspections.actionProofIn", "Proof received")
              : item.status_label}
          </Badge>
          {categoryLabel && (
            <span className="text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
              {categoryLabel}
            </span>
          )}
        </div>
        <p className="whitespace-pre-line text-xs text-[var(--ink)]">
          {item.description}
        </p>
        <p className="flex flex-wrap gap-x-3 text-[11px] text-[var(--ink-3)]">
          <span data-testid="punch-list-item-owner-text">{ownerText}</span>
          {item.due_date && (
            <span
              data-testid="punch-list-item-due"
              className={overdue ? "text-[var(--status-error,#991b1b)]" : undefined}
            >
              {overdue
                ? t("inspections.actionOverdue", "Overdue, due {{date}}", {
                    date: formatDate(item.due_date),
                  })
                : t("inspections.actionDue", "Due {{date}}", {
                    date: formatDate(item.due_date),
                  })}
            </span>
          )}
        </p>
        {item.resolved_at && (
          <p className="text-[10px] text-[var(--ink-3)]">
            Resolved {formatDateTime(item.resolved_at)}
            {item.resolved_by_email ? ` by ${item.resolved_by_email}` : ""}
            {item.resolution_notes ? ` - ${item.resolution_notes}` : ""}
          </p>
        )}
        {item.proof?.length > 0 && <EvidenceList items={item.proof} />}
      </div>
      {isOpen && canResolve && (
        <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
          <Button
            size="sm"
            variant="ghost"
            onClick={onEdit}
            className="min-h-[36px]"
          >
            {t("inspections.actionEdit", "Edit")}
          </Button>
          {proofIn ? (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onReview("reject")}
                className="min-h-[36px]"
              >
                {t("inspections.proofReview.sendBack", "Send back")}
              </Button>
              <Button
                size="sm"
                onClick={() => onReview("approve")}
                className="min-h-[36px]"
              >
                {t("inspections.proofReview.approve", "Approve")}
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={onClose}
              className="min-h-[36px]"
            >
              {t("inspections.punchList.close", "Close")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function RepairTrackingPanel({
  inspectionUuid,
  inspection,
  canEdit,
}: {
  inspectionUuid: string;
  inspection: InspectionDetail;
  canEdit: boolean;
}) {
  const { formatDateTime } = useFormatters();
  const { isPartnerAccess } = useOrg();
  const { data: items, isLoading } = useInspectionPunchList(inspectionUuid);
  // Categories are the tenant's result reasons; an item filed under any
  // other category (one derived from a report finding) shows its own words.
  const { data: choices } = useInspectionChoices();
  const categoryLabels = useMemo(
    () => buildLabelMap(choices?.result_reasons),
    [choices],
  );
  const labelFor = (category: string) =>
    categoryLabels[category] ?? category.replace(/_/g, " ");
  const { t } = useTranslation();
  const ownerLabel = useActionOwnerLabel();
  const ownerText = (item: PunchListItem) => {
    if (!item.owner) return t("inspections.actionNoOwner", "No owner");
    const role = ownerLabel(item.owner, item.owner);
    return item.owner_name ? `${role} - ${item.owner_name}` : role;
  };
  const [editing, setEditing] = useState<PunchListItem | null>(null);
  const createMutation = useCreatePunchListItem(inspectionUuid);
  const close = useClosePunchListWorkItem();
  const queryClient = useQueryClient();
  const [closing, setClosing] = useState<PunchListItem | null>(null);
  const review = useReviewPunchListItem(inspectionUuid);
  const [reviewing, setReviewing] = useState<{
    item: PunchListItem;
    decision: ProofDecision;
  } | null>(null);
  const [newDescription, setNewDescription] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [sendOpen, setSendOpen] = useState(false);
  const sendPreview = usePunchListSendPreview(inspectionUuid, sendOpen);
  const sendMutation = useSendPunchList(inspectionUuid);

  const submitClose = async (
    action: "resolve" | "waive",
    text: string,
    photos: string[],
  ) => {
    if (!closing) return;
    try {
      await close.mutateAsync({
        inspectionUuid,
        itemUuid: closing.uuid,
        action,
        notes: action === "resolve" ? text : undefined,
        photos: action === "resolve" ? photos : undefined,
        reason: action === "waive" ? text : undefined,
      });
      // Closing the last open action can move the inspection on.
      invalidateWorkflowInstanceQueries(
        queryClient,
        inspection.workflow_instance_uuid,
      );
      invalidateVisibleActions(queryClient);
      toast.success(
        action === "resolve"
          ? t("inspections.punchList.resolved", "Item resolved.")
          : t("inspections.punchList.waived", "Item waived."),
      );
      setClosing(null);
    } catch (e) {
      toastApiError(
        e,
        t("inspections.punchList.closeFailed", "Could not close that item."),
      );
    }
  };

  const submitReview = async (notes: string) => {
    if (!reviewing) return;
    try {
      await review.mutateAsync({
        itemUuid: reviewing.item.uuid,
        decision: reviewing.decision,
        notes,
      });
      // Approving the last proof completes the repair; sending proof back
      // returns the inspection to follow-up.
      invalidateWorkflowInstanceQueries(
        queryClient,
        inspection.workflow_instance_uuid,
      );
      invalidateVisibleActions(queryClient);
      toast.success(
        reviewing.decision === "approve"
          ? t("inspections.proofReview.approved", "Repair approved.")
          : t("inspections.proofReview.sentBackDone", "Repair sent back."),
      );
      setReviewing(null);
    } catch (e) {
      toastApiError(
        e,
        t("inspections.proofReview.failed", "Could not save the review."),
      );
    }
  };

  const handleSend = useCallback(async () => {
    try {
      const result = await sendMutation.mutateAsync({});
      const to = [...result.sent];
      toast.success(
        to.length
          ? `Punch list sent to ${to.join(", ")}.`
          : "Punch list send completed.",
      );
      setSendOpen(false);
    } catch (e) {
      toastApiError(e, "Failed to send the punch list.");
    }
  }, [sendMutation]);

  const handleAdd = useCallback(async () => {
    const description = newDescription.trim();
    if (!description) {
      // Surface a clear reason rather than just silently doing nothing.
      // The button used to be disabled which left users wondering why
      // their click did nothing.
      toast.error(
        "Add a description first - what specifically does the contractor need to fix?",
      );
      return;
    }
    try {
      await createMutation.mutateAsync({
        description,
        category: newCategory || undefined,
      });
      setNewDescription("");
      setNewCategory("");
      toast.success("Punch-list item added.");
    } catch (e) {
      toastApiError(e, "Failed to add punch-list item.");
    }
  }, [createMutation, newDescription, newCategory]);

  const openItems = (items ?? []).filter((item) => item.status === "open");
  const closedItems = (items ?? []).filter((item) => item.status !== "open");

  return (
    <div className="flex flex-col gap-5">
      <section className="rounded-lg border border-[var(--rule)] bg-[var(--bg-card)] p-5">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-[var(--ink)]">Punch list</h3>
          <div className="flex items-center gap-2 text-[11px] text-[var(--ink-3)]">
            <Badge variant="destructive">{openItems.length} open</Badge>
            <Badge variant="outline">{closedItems.length} closed</Badge>
            {openItems.length > 0 && (
              <Button
                variant="outline"
                size="xs"
                onClick={() => setSendOpen(true)}
                className="ml-1"
              >
                <Send size={13} className="mr-1.5" />
                Send to contractor
              </Button>
            )}
          </div>
        </div>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 size={16} className="animate-spin text-[var(--ink-3)]" />
          </div>
        ) : (items?.length ?? 0) === 0 ? (
          <p className="mt-3 text-xs text-[var(--ink-3)]">
            No punch-list items yet. Add one below for each defect or corrective
            action the contractor needs to complete.
          </p>
        ) : (
          <div className="mt-3 flex flex-col gap-2">
            {openItems.map((item) => (
              <PunchListItemRow
                key={item.uuid}
                item={item}
                categoryLabel={labelFor(item.category)}
                ownerText={ownerText(item)}
                onEdit={() => setEditing(item)}
                onClose={() => setClosing(item)}
                onReview={(decision) => setReviewing({ item, decision })}
                canResolve={canEdit}
              />
            ))}
            {closedItems.map((item) => (
              <PunchListItemRow
                key={item.uuid}
                item={item}
                categoryLabel={labelFor(item.category)}
                ownerText={ownerText(item)}
                onEdit={() => undefined}
                onClose={() => undefined}
                onReview={() => undefined}
                canResolve={canEdit}
              />
            ))}
          </div>
        )}
      </section>

      {/* A link is an upload capability, so partners never see them. */}
      {!isPartnerAccess &&
        inspection.sub_type?.configuration_data?.follow_up ===
          "actions_with_proof" && (
          <ProofLinksSection inspectionUuid={inspectionUuid} canEdit={canEdit} />
        )}

      <section className="flex flex-col gap-3 rounded-lg border border-[var(--rule)] bg-[var(--bg-card)] p-5">
        {canEdit && (
          <>
            <h3 className="text-sm font-medium text-[var(--ink)]">
              Add a punch-list item
            </h3>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="punch-description">
                Description{" "}
                <span className="text-[var(--status-error,#991b1b)]">*</span>
              </Label>
              <textarea
                id="punch-description"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="e.g. Drip edge missing on north-east elevation, course 4. Re-install per spec."
                className="min-h-[80px] rounded-lg border border-[var(--rule)] bg-[var(--bg)] p-3 text-sm text-[var(--ink)] focus:border-[var(--ink-3)] focus:outline-none"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="punch-category">Category</Label>
              <Select value={newCategory} onValueChange={setNewCategory}>
                <SelectTrigger id="punch-category">
                  <SelectValue placeholder="(optional)" />
                </SelectTrigger>
                <SelectContent>
                  {(choices?.result_reasons ?? []).map((reason) => (
                    <SelectItem key={reason.value} value={reason.value}>
                      {reason.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end pt-1">
              <Button onClick={handleAdd} disabled={createMutation.isPending}>
                {createMutation.isPending ? (
                  <>
                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                    Adding…
                  </>
                ) : (
                  "Add item"
                )}
              </Button>
            </div>
          </>
        )}
        {inspection.repair_completed_at && (
          <p className="mt-2 text-[11px] text-[var(--ink-3)]">
            Repair completed: {formatDateTime(inspection.repair_completed_at)}
          </p>
        )}
      </section>

      <PunchListItemDialog
        inspection={inspection}
        item={editing}
        onClose={() => setEditing(null)}
      />

      {reviewing && (
        <ReviewProofDialog
          item={reviewing.item}
          decision={reviewing.decision}
          onOpenChange={(next) => !next && setReviewing(null)}
          onSubmit={submitReview}
          isPending={review.isPending}
        />
      )}

      <ClosePunchListItemDialog
        item={closing}
        inspectionUuid={inspectionUuid}
        open={closing !== null}
        onOpenChange={(next) => !next && setClosing(null)}
        onSubmit={submitClose}
        isPending={close.isPending}
      />

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send punch list to contractor</DialogTitle>
            <DialogDescription>
              {/* Who it goes to is the tenant's punch-list trigger: shown below. */}
              {t(
                "inspections.punchList.sendHint",
                "Emails the open punch-list items below to the people shown.",
              )}
            </DialogDescription>
          </DialogHeader>

          {sendPreview.isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={16} className="animate-spin text-[var(--ink-3)]" />
            </div>
          ) : sendPreview.isError ? (
            <div className="rounded-lg border border-[var(--rule)] bg-[var(--bg-light)] p-3 text-xs text-[var(--status-error,#991b1b)]">
              Failed to load preview. Close and try again, or check your
              connection.
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {sendPreview.data && !sendPreview.data.can_send ? (
                <p className="rounded-lg border border-[var(--rule)] bg-[var(--bg-light)] p-3 text-xs text-[var(--ink-2)]">
                  {sendPreview.data.blocked_reason}
                </p>
              ) : (
                sendPreview.data && (
                  <div className="flex flex-col gap-1.5 text-xs">
                    <div className="flex gap-2">
                      <span className="w-8 shrink-0 text-[var(--ink-3)]">
                        To
                      </span>
                      <span className="text-[var(--ink)]">
                        {sendPreview.data.recipients_to.join(", ")}
                      </span>
                    </div>
                    {sendPreview.data.recipients_cc.length > 0 && (
                      <div className="flex gap-2">
                        <span className="w-8 shrink-0 text-[var(--ink-3)]">
                          Cc
                        </span>
                        <span className="text-[var(--ink-2)]">
                          {sendPreview.data.recipients_cc.join(", ")}
                        </span>
                      </div>
                    )}
                  </div>
                )
              )}

              <div className="flex flex-col gap-2">
                <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--ink-3)]">
                  {openItems.length} item{openItems.length === 1 ? "" : "s"} to
                  send
                </p>
                <ul className="flex max-h-56 flex-col gap-2 overflow-y-auto">
                  {openItems.map((item) => (
                    <li
                      key={item.uuid}
                      className="flex items-start gap-2 rounded-lg border border-[var(--rule)] bg-[var(--bg-card)] p-3"
                    >
                      {item.category && (
                        <Badge variant="outline" className="shrink-0">
                          {labelFor(item.category)}
                        </Badge>
                      )}
                      <span className="text-xs text-[var(--ink)]">
                        {item.description}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setSendOpen(false)}
              disabled={sendMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSend}
              disabled={
                sendMutation.isPending ||
                sendPreview.isLoading ||
                !sendPreview.data?.can_send
              }
            >
              {sendMutation.isPending ? (
                <>
                  <Loader2 size={14} className="mr-1.5 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <Send size={14} className="mr-1.5" />
                  Send punch list
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Inspector evidence-upload dialog. Two-step flow under the hood:
 *
 *   1. POST /api/files/upload/  (multipart, use_case=INSPECTION_EVIDENCE)
 *      → returns a UserUploadedFile with a stable uuid.
 *   2. POST /api/inspections/<uuid>/evidence/  with that file_uuid
 *      plus kind + caption + (optional) GPS / captured_at.
 *
 * The file ``<input>`` uses ``capture="environment"`` so on mobile
 * Safari / Chrome the OS surfaces the rear-camera shortcut alongside
 * the photo library; desktop users get the normal file picker. PDFs +
 * Excel + photos are all accepted because the inspector report flow
 * needs all three.
 */
// Evidence kinds are served by the choices API; no hardcoded list.

function AddEvidenceDialog({
  onClose,
  inspectionUuid,
}: {
  onClose: () => void;
  inspectionUuid: string | undefined;
}) {
  const { data: choices } = useInspectionChoices();
  const evidenceKinds = choices?.evidence_kinds ?? [];
  const uploadFile = useUploadFile();
  const attach = useUploadInspectionEvidence(inspectionUuid);
  const [added, setAdded] = useState<UploadedFileItem[]>([]);
  const [kind, setKind] = useState<string>("photo");
  const [caption, setCaption] = useState("");

  // One-step-per-file, matching the claim Evidence flow: the classifier
  // (kind + caption) is chosen first, then each dropped file uploads to
  // storage and attaches to the inspection in a single pass.
  const uploadFn = async (file: File): Promise<UploadedFileItem> => {
    try {
      const r = await uploadFile.mutateAsync({
        file,
        use_case: "INSPECTION_EVIDENCE",
      });
      await attach.mutateAsync({
        file_uuid: r.uuid,
        kind,
        caption: caption.trim() || undefined,
        captured_at: new Date().toISOString(),
      });
      return {
        uuid: r.uuid,
        file_name: r.file_name ?? file.name,
        file_url: r.file_url,
        mime_type: (r as { mime_type?: string }).mime_type,
      };
    } catch (e) {
      toastApiError(e, "Failed to add evidence.");
      throw e;
    }
  };

  const close = () => {
    setAdded([]);
    setCaption("");
    setKind("photo");
    onClose();
  };

  const classifier = (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <div className="flex flex-col gap-1.5">
        <Label className="text-[12px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
          Kind
        </Label>
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger>
            <SelectValue placeholder="Pick a kind" />
          </SelectTrigger>
          <SelectContent>
            {evidenceKinds.map((k) => (
              <SelectItem key={k.value} value={k.value}>
                {k.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label
          htmlFor="evidence-caption"
          className="text-[12px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]"
        >
          Caption
        </Label>
        <Input
          id="evidence-caption"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder="e.g. North-west drip edge, course 4"
        />
      </div>
    </div>
  );

  return (
    <AddEvidenceDialogShell
      title="Add evidence"
      description="Upload a photo, PDF, or spreadsheet captured on-site. On mobile, the file picker offers your camera as well as the photo library."
      classifier={classifier}
      value={added}
      onChange={setAdded}
      uploadFn={uploadFn}
      accept="image/jpeg,image/png,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
      acceptLabel="JPG, PNG, PDF, XLS, XLSX, CSV"
      multiple
      footerNote="Capture timestamp is set automatically. GPS coordinates + line-item tagging will land with the dedicated capture flow on mobile."
      onClose={close}
    />
  );
}

/**
 * Edit-schedule dialog - date and time-of-day are captured separately
 * so the Coordinator can adjust either without re-entering the other.
 * The combined value is sent to the backend as a single
 * ``scheduled_for`` ISO datetime. Clear both to unschedule (sets
 * ``scheduled_for = null`` server-side).
 */
/**
 * Splits an ISO datetime into ``{ date: 'YYYY-MM-DD', time: 'HH:MM' }``
 * using the user's local timezone, or returns empties for null/invalid.
 * Used by the EditScheduleDialog to seed its inputs on mount.
 */
function splitScheduledFor(value: string | null): {
  date: string;
  time: string;
} {
  if (!value) return { date: "", time: "" };
  const dt = new Date(value);
  if (isNaN(dt.getTime())) return { date: "", time: "" };
  const yyyy = String(dt.getFullYear()).padStart(4, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  const hh = String(dt.getHours()).padStart(2, "0");
  const mi = String(dt.getMinutes()).padStart(2, "0");
  return { date: `${yyyy}-${mm}-${dd}`, time: `${hh}:${mi}` };
}

function EditScheduleDialog({
  open,
  onOpenChange,
  initialScheduledFor,
  onSave,
  isPending,
  reasonRequired = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Current ``scheduled_for`` value as an ISO datetime (or null). */
  initialScheduledFor: string | null;
  /** Combined ISO datetime or null on save, plus an optional reschedule reason. */
  onSave: (scheduled_for: string | null, reason?: string) => Promise<void>;
  isPending: boolean;
  /**
   * When true the dialog is driving the ``reschedule`` workflow action rather
   * than a plain schedule correction: a new slot AND a reason are mandatory
   * (unscheduling is not allowed), and the copy reflects the reschedule.
   * Same modal, same source (``scheduled_for``) - only the save path differs.
   */
  reasonRequired?: boolean;
}) {
  // Seeded once from the inspection's current schedule. Parent
  // conditionally mounts this component so a fresh mount = fresh seed.
  const initial = splitScheduledFor(initialScheduledFor);
  const [date, setDate] = useState(initial.date);
  const [time, setTime] = useState(initial.time);
  const [reason, setReason] = useState("");

  const handleSave = async () => {
    if (reasonRequired) {
      // Reschedule: a new slot and a reason are both mandatory.
      if (!date) {
        toast.error("Pick a new date for the appointment.");
        return;
      }
      if (!reason.trim()) {
        toast.error("Add a reason for the reschedule.");
        return;
      }
      const safeTime = time || "09:00";
      const isoUtc = new Date(`${date}T${safeTime}:00`).toISOString();
      await onSave(isoUtc, reason.trim());
      return;
    }
    if (!date && !time) {
      // User cleared both - unschedule.
      await onSave(null);
      return;
    }
    if (!date) {
      toast.error("Pick a date (or clear time to unschedule entirely).");
      return;
    }
    // Default time to 09:00 if the user left it blank but set a date.
    const safeTime = time || "09:00";
    // Send as UTC ISO so the round trip doesn't drift by the user's
    // timezone offset - see the matching note on the wizard submit.
    const isoUtc = new Date(`${date}T${safeTime}:00`).toISOString();
    await onSave(isoUtc);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {reasonRequired ? "Reschedule inspection" : "Edit schedule"}
          </DialogTitle>
          <DialogDescription>
            {reasonRequired
              ? "Pick the new appointment slot and note why it moved. "
              : "Adjust the date or time-of-day. "}
            Need to see the inspector&apos;s week-view first?{" "}
            <Link
              to={
                date
                  ? `/inspections/scheduling?date=${date}`
                  : "/inspections/scheduling"
              }
              className="underline-offset-2 hover:underline"
            >
              Open the Scheduling Console
            </Link>
            .
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-2">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto]">
            <div className="flex flex-col gap-1.5">
              <Label>Date</Label>
              <DatePicker
                value={date}
                onChange={setDate}
                placeholder="Pick a date"
                disablePast
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Time</Label>
              <TimeSelect
                value={time}
                onChange={setTime}
                ariaLabel="Scheduled time"
                className="sm:w-[160px]"
              />
            </div>
          </div>
          {reasonRequired ? (
            <div className="flex flex-col gap-1.5">
              <Label>Reason</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why is the appointment moving?"
                rows={3}
              />
            </div>
          ) : (
            <p className="text-[11px] text-[var(--ink-3)]">
              Clear both fields to unschedule the inspection (drops it back to
              draft for the Coordinator to re-plan).
            </p>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isPending}>
            {isPending ? (
              <>
                <Loader2 size={14} className="mr-1.5 animate-spin" />
                Saving…
              </>
            ) : (
              "Save schedule"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Header controls that sit next to the workflow action buttons on the
 * inspection detail page. Three related capabilities Amanda's dashboard
 * spec depends on, styled to match the claims header:
 *
 *  - **Inspector pill** - the field tech who performs the work. Interactive
 *    (search + reassign) via the `assign-inspector` endpoint; distinct from
 *    the follow-up assignee.
 *  - **Follow-up pill** - the user flagged to take the next action (drives
 *    the "Awaiting Follow Up" tile).
 *  - **Confirm control** - appointment confirmation (drives the "confirmed /
 *    unconfirmed" column + the RED-within-7-days treatment). Opens a
 *    confirmation dialog restating the scheduled date/time so the coordinator
 *    knows exactly which appointment they are confirming.
 *
 * Read-only (non-edit) users see the same information as plain badges.
 */
function InspectionHeaderControls({
  inspection,
  canEdit,
  closed,
}: {
  inspection: InspectionDetail;
  canEdit: boolean;
  /** The workflow has reached a terminal state (closed, cancelled). */
  closed: boolean;
}) {
  const { formatDateTime } = useFormatters();
  const { can } = usePermissions();
  const canMarkPriority = can("inspections.mark_priority");
  const priorityMutation = useMarkInspectionPriority(inspection.uuid);
  const confirmMutation = useConfirmAppointment(inspection.uuid);
  const followUpMutation = useAssignFollowUp(inspection.uuid);
  const inspectorMutation = useAssignInspector(inspection.uuid);

  const [followUpSearch, setFollowUpSearch] = useState("");
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const { data: followUpResults, isLoading: followUpLoading } =
    useInspectorSearch(followUpSearch);

  const [inspectorSearch, setInspectorSearch] = useState("");
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const { data: inspectorResults, isLoading: inspectorLoading } =
    useInspectorSearch(inspectorSearch);

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const confirmed = inspection.appointment_confirmed;
  const confirmedAt = inspection.appointment_confirmed_at
    ? formatDateTime(inspection.appointment_confirmed_at)
    : "";
  // There is nothing to confirm until an appointment has actually been
  // booked. A draft/unscheduled inspection has no `scheduled_for`, so the
  // Confirm control stays disabled - confirming it would leave a "Confirmed"
  // badge over a still-in-draft workflow (the mismatch a coordinator hit).
  const hasAppointment = !!inspection.scheduled_for;
  const scheduledLabel = hasAppointment
    ? formatDateTime(inspection.scheduled_for as string)
    : "";

  const followUpUuid = inspection.assigned_to_uuid || "";
  const followUpLabel =
    inspection.assigned_to_name || inspection.assigned_to_email || "";
  const inspectorUuid = inspection.inspector_uuid || "";
  const inspectorLabel =
    inspection.inspector_name || inspection.inspector_email || "";

  // Groups for the shared picker. The current selection is injected so the
  // trigger resolves the person's name even before a search runs (server-side
  // typeahead only returns matches for the typed term).
  const buildGroups = useCallback(
    (
      results: { uuid: string; full_name: string; email: string }[] | undefined,
      currentUuid: string,
      currentLabel: string,
      currentEmail: string,
    ): SearchableSelectGroup[] => {
      const options = (results ?? []).map((o) => ({
        value: o.uuid,
        label: o.full_name || o.email,
        searchText: o.email,
      }));
      if (currentUuid && !options.some((o) => o.value === currentUuid)) {
        options.unshift({
          value: currentUuid,
          label: currentLabel || currentUuid,
          searchText: currentEmail,
        });
      }
      return [{ label: "Team members", options }];
    },
    [],
  );

  const inspectorGroups = useMemo(
    () =>
      buildGroups(
        inspectorResults,
        inspectorUuid,
        inspectorLabel,
        inspection.inspector_email || "",
      ),
    [
      buildGroups,
      inspectorResults,
      inspectorUuid,
      inspectorLabel,
      inspection.inspector_email,
    ],
  );

  const followUpGroups = useMemo(
    () =>
      buildGroups(
        followUpResults,
        followUpUuid,
        followUpLabel,
        inspection.assigned_to_email || "",
      ),
    [
      buildGroups,
      followUpResults,
      followUpUuid,
      followUpLabel,
      inspection.assigned_to_email,
    ],
  );

  const handleAssignInspector = useCallback(
    async (uuid: string) => {
      try {
        await inspectorMutation.mutateAsync({ inspector_uuid: uuid });
        toast.success(uuid ? "Inspector updated." : "Inspector cleared.");
      } catch (e) {
        toastApiError(e, "Failed to update inspector.");
      }
    },
    [inspectorMutation],
  );

  const handleAssignFollowUp = useCallback(
    async (uuid: string) => {
      try {
        await followUpMutation.mutateAsync({ assignee_uuid: uuid });
        toast.success(
          uuid ? "Follow-up assignee updated." : "Follow-up cleared.",
        );
      } catch (e) {
        toastApiError(e, "Failed to update follow-up assignee.");
      }
    },
    [followUpMutation],
  );

  const handleConfirm = useCallback(async () => {
    try {
      await confirmMutation.mutateAsync({ confirm: true });
      toast.success("Appointment confirmed.");
      setConfirmDialogOpen(false);
    } catch (e) {
      toastApiError(e, "Failed to confirm appointment.");
    }
  }, [confirmMutation]);

  const handleUnconfirm = useCallback(async () => {
    try {
      await confirmMutation.mutateAsync({ confirm: false });
      toast.success("Appointment unconfirmed.");
    } catch (e) {
      toastApiError(e, "Failed to unconfirm appointment.");
    }
  }, [confirmMutation]);

  if (!canEdit || closed) {
    // Read-only fallback - the same information as static badges. A closed
    // inspection has nothing left to assign or confirm.
    return (
      <>
        {inspectorLabel ? (
          <Badge variant="secondary">
            <HardHat size={12} className="mr-1" />
            {inspectorLabel}
          </Badge>
        ) : null}
        {followUpLabel ? (
          <Badge variant="secondary">
            <CornerDownRight size={12} className="mr-1" />
            {followUpLabel}
          </Badge>
        ) : null}
        {confirmed ? (
          <Badge
            variant="outline"
            className="border-[var(--status-ok-border)] bg-[var(--status-ok-bg)] text-[var(--status-ok)]"
          >
            <CheckCircle2 size={12} className="mr-1" />
            Confirmed
          </Badge>
        ) : closed ? null : (
          <Badge variant="destructive">
            <XCircle size={12} className="mr-1" />
            Unconfirmed
          </Badge>
        )}
      </>
    );
  }

  return (
    <>
      {/* Inspector - the field tech who performs the work. */}
      <AssignmentPill
        value={inspectorUuid}
        label={inspectorLabel}
        placeholder="Assign inspector"
        groups={inspectorGroups}
        searchValue={inspectorSearch}
        onSearchChange={setInspectorSearch}
        onOpenChange={setInspectorOpen}
        loading={inspectorOpen && inspectorLoading}
        searchPlaceholder="Search inspectors..."
        emptyMessage="No inspectors found"
        onAssign={handleAssignInspector}
        pending={inspectorMutation.isPending}
        icon={<HardHat className="h-3 w-3" />}
        data-testid="inspector-assignment-pill"
      />

      {/* Follow-up - the user flagged to take the next action. */}
      <AssignmentPill
        value={followUpUuid}
        label={followUpLabel}
        placeholder="Assign follow-up"
        groups={followUpGroups}
        searchValue={followUpSearch}
        onSearchChange={setFollowUpSearch}
        onOpenChange={setFollowUpOpen}
        loading={followUpOpen && followUpLoading}
        searchPlaceholder="Search team members..."
        emptyMessage="No members found"
        onAssign={handleAssignFollowUp}
        pending={followUpMutation.isPending}
        icon={<CornerDownRight className="h-3 w-3" />}
        data-testid="follow-up-assignment-pill"
      />

      {/* Appointment confirmation. */}
      {confirmed ? (
        <Button
          variant="outline"
          size="sm"
          onClick={handleUnconfirm}
          disabled={confirmMutation.isPending}
          className="flex cursor-pointer items-center gap-1 rounded-[6px] border-[var(--status-ok-border)] bg-[var(--status-ok-bg)] px-3 py-1.5 text-[11px] text-[var(--status-ok)] transition-all duration-150"
          title={confirmedAt ? `Confirmed ${confirmedAt}` : undefined}
          data-testid="appointment-confirm-toggle"
        >
          {confirmMutation.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <CheckCircle2 className="h-3 w-3" />
          )}
          Confirmed
        </Button>
      ) : (
        <Button
          variant="default"
          size="sm"
          onClick={() => setConfirmDialogOpen(true)}
          disabled={confirmMutation.isPending || !hasAppointment}
          className="flex cursor-pointer items-center gap-1 rounded-[6px] px-3 py-1.5 text-[11px] transition-all duration-150"
          title={
            !hasAppointment
              ? "Schedule the inspection before confirming the appointment."
              : undefined
          }
          data-testid="appointment-confirm-toggle"
        >
          <CheckCircle2 className="h-3 w-3" />
          Confirm appointment
        </Button>
      )}

      {/* Priority marking. Gated on the narrow `inspections.mark_priority`
          codename, NOT on canEdit - a Resolution Specialist can escalate a
          home without holding the coarse edit permission. Sized like the
          other header actions rather than a full-width block. */}
      {canMarkPriority && (
        <Button
          variant={inspection.is_priority ? "default" : "outline"}
          size="sm"
          onClick={() =>
            priorityMutation.mutate({
              is_priority: !inspection.is_priority,
            })
          }
          disabled={priorityMutation.isPending}
          className="flex cursor-pointer items-center gap-1 rounded-[6px] px-3 py-1.5 text-[11px] transition-all duration-150"
          title={
            inspection.priority_reason ||
            (inspection.is_priority
              ? "Remove the priority mark"
              : "Flag this inspection as urgent")
          }
          data-testid="inspection-priority-toggle"
        >
          <Flag className="h-3 w-3" />
          {inspection.is_priority ? "Priority" : "Mark priority"}
        </Button>
      )}

      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm appointment</DialogTitle>
            <DialogDescription>
              Confirm that this appointment is booked with the customer.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 rounded-lg border border-[var(--rule)] bg-[var(--bg-light)] p-4">
            <div className="flex items-start gap-2.5">
              <CalendarCheck
                size={16}
                className="mt-0.5 shrink-0 text-[var(--ink-3)]"
              />
              <div className="flex flex-col">
                <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--ink-3)]">
                  Scheduled for
                </span>
                <span className="text-[14px] text-[var(--ink)]">
                  {scheduledLabel || "Not scheduled"}
                </span>
              </div>
            </div>
            {inspection.consumer_name ? (
              <div className="flex items-start gap-2.5">
                <User
                  size={16}
                  className="mt-0.5 shrink-0 text-[var(--ink-3)]"
                />
                <div className="flex flex-col">
                  <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--ink-3)]">
                    Customer
                  </span>
                  <span className="text-[14px] text-[var(--ink)]">
                    {inspection.consumer_name}
                  </span>
                </div>
              </div>
            ) : null}
            {inspection.property_address_line_1 ? (
              <div className="flex items-start gap-2.5">
                <MapPin
                  size={16}
                  className="mt-0.5 shrink-0 text-[var(--ink-3)]"
                />
                <div className="flex flex-col">
                  <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--ink-3)]">
                    Property
                  </span>
                  <span className="text-[14px] text-[var(--ink)]">
                    {[
                      inspection.property_address_line_1,
                      inspection.property_city,
                      inspection.property_state,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </span>
                </div>
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDialogOpen(false)}
              disabled={confirmMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={confirmMutation.isPending || !hasAppointment}
            >
              {confirmMutation.isPending ? (
                <>
                  <Loader2 size={14} className="mr-1.5 animate-spin" />
                  Confirming…
                </>
              ) : (
                <>
                  <CheckCircle2 size={14} className="mr-1.5" />
                  Confirm appointment
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function InspectionDetailPage() {
  const { t } = useTranslation();
  const cardsView = usePinnedView("inspection-detail");
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const { can } = usePermissions();
  // Editing/scheduling/cancelling an inspection requires an inspections
  // edit permission; view-only roles (Lab / RS / Viewer / Agent) see the
  // detail page read-only. The API enforces the same gate server-side.
  const canEditInspection =
    can("inspections.edit.global") || can("inspections.edit.acl");
  const { data: inspection, isLoading, refetch } = useInspection(uuid);
  // Engine-authoritative current state (label + advisory colour + terminal
  // flag) for the status banner. The instance API exposes only the current
  // state, not the full graph - the inspection lifecycle is a branching DAG
  // (passed / failed / repair_pending / not_completed all diverge from
  // in_progress) that a linear stepper cannot faithfully represent, so we
  // render the authoritative current state via the shared WorkflowStateBadge
  // rather than a hardcoded step list.
  const { data: workflowInstance } = useWorkflowInstance(
    inspection?.workflow_instance_uuid,
  );
  const { data: choices } = useInspectionChoices();
  const { resultLabels, reasonLabels, cancellationLabels } = useMemo(
    () => ({
      resultLabels: buildLabelMap(choices?.results),
      reasonLabels: buildLabelMap(choices?.result_reasons),
      cancellationLabels: buildLabelMap(choices?.cancellation_reasons),
    }),
    [choices],
  );
  const updateMutation = useUpdateInspection(uuid);
  const [scheduleEditOpen, setScheduleEditOpen] = useState(false);
  // When the schedule modal is opened to drive a reschedule workflow action
  // (rather than the pencil "Edit schedule" direct PATCH), this holds the
  // action key so Save runs the engine transition instead - same modal, same
  // source (scheduled_for), only the persistence path differs.
  const [rescheduleAction, setRescheduleAction] = useState<string | null>(null);
  const executeMutation = useExecuteWorkflowAction(
    inspection?.workflow_instance_uuid ?? "",
  );
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [sendingForm, setSendingForm] = useState(false);
  // The Forms card shows only once it has a use: a published form that can be
  // sent from an inspection, or forms already sent from this one. Forms are
  // internal, so partners never see it.
  const { isPartnerAccess } = useOrg();
  const { data: inspectionForms = [] } = useFormTemplates({
    recordType: "inspection",
    activeOnly: true,
    publishedOnly: true,
  });
  const { data: sentForms = [] } = useRecordForms("inspection", uuid);
  const showForms =
    !isPartnerAccess && (inspectionForms.length > 0 || sentForms.length > 0);
  // The Checklist tab shows once there is a checklist to fill or already
  // filled: the type names one, or this inspection started one. It is the
  // team's working record, so partners never see it.
  const { data: checklist } = useInspectionChecklist(uuid, !isPartnerAccess);
  // The report prints the checklist once it locks, as the result is recorded.
  const downloadReport = useDownloadInspectionReport(uuid);
  const [sharingReport, setSharingReport] = useState(false);
  const queryClient = useQueryClient();
  const showChecklistTab =
    !isPartnerAccess && !!checklist && checklist.status !== "none";
  // The type's safety check, filled before work starts: internal, like the
  // checklist.
  const { data: safety } = useInspectionChecklist(
    uuid,
    !isPartnerAccess,
    "safety",
  );
  const hasSafetyCheck =
    !isPartnerAccess && !!safety && safety.status !== "none";
  // Resource lookup providers back the ENTITY_SEARCH capture fields on the
  // workflow action dialog (e.g. the inspector picker on the schedule action).
  const resourceProviders = useResourceLookupProviders();

  // The inspection's notifications for the current user are surfaced in a
  // persistent section (NotificationsPanel), mirroring the claim detail page.
  // We deliberately do NOT mark them read on open - they stay unread (and keep
  // driving the inspections-list dot and new/unopened emphasis, per-user and
  // cross-device) until the user explicitly marks them read here, or the
  // standing condition clears itself. See docs/notification-infrastructure.md.
  const inspectionUuidForNotifications = inspection?.uuid ?? null;
  const { data: unreadNotificationsData } = useNotifications(
    inspectionUuidForNotifications
      ? {
          record_type: "inspection",
          record_uuid: inspectionUuidForNotifications,
          status: "unread",
        }
      : undefined,
    { enabled: !!inspectionUuidForNotifications },
  );
  // Read notifications are the collapsible "history". Fetched up front so the
  // panel stays visible once every notification has been read: the section
  // should only disappear when the inspection has no notifications at all.
  const { data: readNotificationsData } = useNotifications(
    inspectionUuidForNotifications
      ? {
          record_type: "inspection",
          record_uuid: inspectionUuidForNotifications,
          status: "read",
        }
      : undefined,
    { enabled: !!inspectionUuidForNotifications },
  );
  const unreadNotifications = unreadNotificationsData?.results ?? [];
  const readNotifications = readNotificationsData?.results ?? [];
  const markNotificationsRead = useMarkNotificationsRead();
  const notificationItemAction = useNotificationItemAction();

  const markNotificationRead = (notificationUuid: string) => {
    notificationItemAction.mutate(
      { uuid: notificationUuid, action: "read" },
      {
        onError: (err) =>
          toastApiError(err, "Failed to mark notification read"),
      },
    );
  };

  const markAllNotificationsRead = () => {
    if (!inspectionUuidForNotifications) return;
    markNotificationsRead.mutate(
      {
        record_type: "inspection",
        record_uuid: inspectionUuidForNotifications,
      },
      {
        onError: (err) =>
          toastApiError(err, "Failed to mark notifications read"),
      },
    );
  };

  const dismissNotification = (notificationUuid: string) => {
    notificationItemAction.mutate(
      { uuid: notificationUuid, action: "archive" },
      {
        onError: (err) => toastApiError(err, "Failed to dismiss notification"),
      },
    );
  };

  const handleSaveSchedule = useCallback(
    async (scheduled_for: string | null, reason?: string) => {
      // Reschedule mode: drive the engine transition so the reschedule
      // counter bumps and the reason is captured - same modal, same
      // scheduled_for source, but routed through apps.workflows.
      if (rescheduleAction) {
        try {
          await executeMutation.mutateAsync({
            action_key: rescheduleAction,
            captured_values: {
              new_scheduled_for: scheduled_for,
              reschedule_reason: reason,
            },
          });
          toast.success("Inspection rescheduled.");
          setScheduleEditOpen(false);
          setRescheduleAction(null);
          refetch();
        } catch (e) {
          toastApiError(e, "Failed to reschedule inspection.");
        }
        return;
      }
      // Plain "Edit schedule" pencil: direct correction of the slot.
      try {
        await updateMutation.mutateAsync({ scheduled_for });
        toast.success("Schedule updated.");
        setScheduleEditOpen(false);
      } catch (e) {
        toastApiError(e, "Failed to update schedule.");
      }
    },
    [updateMutation, executeMutation, rescheduleAction, refetch],
  );

  const schedulingItems: CardFieldItem[] = useMemo(() => {
    if (!inspection) return [];
    return [
      {
        field: defineField({
          code_name: "scheduled_for",
          display_name: "Scheduled for",
          field_type: "DATE_TIME",
        }),
        value: inspection.scheduled_for,
      },
      {
        field: defineField({
          code_name: "requested_at",
          display_name: "Requested at",
          field_type: "DATE_TIME",
        }),
        value: inspection.requested_at,
      },
      {
        field: defineField({
          code_name: "rescheduled_count",
          display_name: "Times rescheduled",
          field_type: "NUMBER",
        }),
        value: inspection.rescheduled_count,
      },
      // Inspector now lives as a prominent, interactive assignment pill in
      // the page header (next to the workflow actions) - dropped here to
      // avoid duplicating it in the scheduling card.
    ];
  }, [inspection]);

  const consumerItems: CardFieldItem[] = useMemo(() => {
    if (!inspection) return [];
    return [
      {
        field: defineField({
          code_name: "consumer_name",
          display_name: "Name",
          field_type: "TEXT",
        }),
        value: inspection.consumer_name,
      },
      {
        field: defineField({
          code_name: "consumer_email",
          display_name: "Email",
          field_type: "EMAIL",
        }),
        value: inspection.consumer_email,
      },
      {
        field: defineField({
          code_name: "consumer_phone",
          display_name: "Phone",
          field_type: "PHONE",
        }),
        value: inspection.consumer_phone,
      },
    ];
  }, [inspection]);

  /**
   * What the claim says is wrong, so the inspection team can judge urgency
   * and know what to look at without opening the claim. Only the keys the
   * claim actually carries appear - a tenant that does not capture a defect
   * simply shows fewer rows.
   */
  const claimContextItems: CardFieldItem[] = useMemo(() => {
    const ctx = inspection?.claim_context;
    if (!ctx) return [];
    const rows: Array<[string, string, unknown]> = [
      ["claim_type_label", "Claim type", ctx.claim_type_label],
      ["defect", "Defect", ctx.defect],
      ["defect_detail", "Defect detail", ctx.defect_detail],
      ["product_impacted", "Product impacted", ctx.product_impacted],
      ["description", "What was reported", ctx.description],
    ];
    return rows
      .filter(([, , value]) => value != null && value !== "")
      .map(([code, label, value]) => ({
        field: defineField({
          code_name: code,
          display_name: label,
          field_type: "TEXT",
        }),
        value,
      }));
  }, [inspection]);

  const propertyItems: CardFieldItem[] = useMemo(() => {
    if (!inspection) return [];
    const items: CardFieldItem[] = [
      {
        field: defineField({
          code_name: "property_address_line_1",
          display_name: "Address",
          field_type: "TEXT",
        }),
        value: inspection.property_address_line_1,
      },
    ];
    if (inspection.property_address_line_2) {
      items.push({
        field: defineField({
          code_name: "property_address_line_2",
          display_name: "Address line 2",
          field_type: "TEXT",
        }),
        value: inspection.property_address_line_2,
      });
    }
    items.push(
      {
        field: defineField({
          code_name: "property_city",
          display_name: "City",
          field_type: "TEXT",
        }),
        value: inspection.property_city,
      },
      {
        field: defineField({
          code_name: "property_state",
          display_name: "State",
          field_type: "TEXT",
        }),
        value: inspection.property_state,
      },
      {
        field: defineField({
          code_name: "property_postal_code",
          display_name: "Postal code",
          field_type: "TEXT",
        }),
        value: inspection.property_postal_code,
      },
    );
    if (inspection.property_country) {
      items.push({
        field: defineField({
          code_name: "property_country",
          display_name: "Country",
          field_type: "TEXT",
        }),
        value: inspection.property_country,
      });
    }
    return items;
  }, [inspection]);

  const linkageItems: CardFieldItem[] = useMemo(() => {
    if (!inspection) return [];
    const items: CardFieldItem[] = [
      {
        field: defineField({
          code_name: "contractor_name",
          display_name: "Contractor",
          field_type: "TEXT",
        }),
        value: inspection.contractor_name,
      },
      {
        field: defineField({
          code_name: "claim_ref",
          display_name: "Linked claim",
          field_type: "TEXT",
        }),
        value: inspection.claim_ref
          ? `${inspection.product_name ? inspection.product_name + " - " : ""}${inspection.claim_ref}`
          : null,
        readOnly: true,
        ...(inspection.claim_uuid && inspection.claim_ref
          ? {
              link: true,
              onLinkClick: () => navigate(`/claims/${inspection.claim_uuid}`),
            }
          : {}),
      },
    ];
    if (inspection.holding_uuid) {
      items.push({
        field: defineField({
          code_name: "holding_uuid",
          display_name: "Linked registration",
          field_type: "TEXT",
        }),
        value: "View registration",
        readOnly: true,
        link: true,
        onLinkClick: () =>
          navigate(`/registrations/${inspection.holding_uuid}`),
      });
    }
    if (inspection.warranty_uuid) {
      items.push({
        field: defineField({
          code_name: "warranty_uuid",
          display_name: "Linked warranty",
          field_type: "TEXT",
        }),
        value: "View warranty",
        readOnly: true,
        link: true,
        onLinkClick: () => navigate(`/warranties/${inspection.warranty_uuid}`),
      });
    }
    items.push({
      field: defineField({
        code_name: "trigger_source",
        display_name: "Trigger source",
        field_type: "TEXT",
      }),
      value: inspection.trigger_source
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase()),
    });
    if (inspection.trigger_notes) {
      items.push({
        field: defineField({
          code_name: "trigger_notes",
          display_name: "Trigger notes",
          field_type: "TEXTAREA",
        }),
        value: inspection.trigger_notes,
      });
    }
    return items;
  }, [inspection, navigate]);

  const resultItems: CardFieldItem[] = useMemo(() => {
    if (!inspection || !inspection.result) return [];
    return [
      {
        field: defineField({
          code_name: "result",
          display_name: "Result",
          field_type: "TEXT",
        }),
        value: resultLabels[inspection.result] ?? inspection.result,
      },
      {
        field: defineField({
          code_name: "result_reason",
          display_name: "Failure reason",
          field_type: "TEXT",
        }),
        value:
          reasonLabels[inspection.result_reason] ?? inspection.result_reason,
      },
      {
        field: defineField({
          code_name: "inspector_notes",
          display_name: "Inspector notes",
          field_type: "TEXTAREA",
        }),
        value: inspection.inspector_notes,
      },
      {
        field: defineField({
          code_name: "completed_at",
          display_name: "Completed at",
          field_type: "DATE_TIME",
        }),
        value: inspection.completed_at,
      },
    ];
  }, [inspection, resultLabels, reasonLabels]);

  const cancelItems: CardFieldItem[] = useMemo(() => {
    if (!inspection || inspection.lifecycle_status !== "cancelled") return [];
    return [
      {
        field: defineField({
          code_name: "cancellation_reason_code",
          display_name: "Reason",
          field_type: "TEXT",
        }),
        value:
          cancellationLabels[inspection.cancellation_reason_code] ??
          inspection.cancellation_reason_code,
      },
      {
        field: defineField({
          code_name: "cancelled_at",
          display_name: "Cancelled at",
          field_type: "DATE_TIME",
        }),
        value: inspection.cancelled_at,
      },
      {
        field: defineField({
          code_name: "cancellation_notes",
          display_name: "Notes",
          field_type: "TEXTAREA",
        }),
        value: inspection.cancellation_notes,
      },
    ];
  }, [inspection, cancellationLabels]);

  const attributeItems: CardFieldItem[] = useMemo(() => {
    if (!inspection) return [];
    const vals = inspection.attribute_values;
    if (!vals || typeof vals !== "object") return [];
    return Object.entries(vals)
      .filter(([, v]) => v !== null && v !== undefined && v !== "")
      .map(([key, v]) => ({
        field: defineField({
          code_name: key,
          display_name: key
            .replace(/_/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase()),
          field_type: "TEXT" as const,
        }),
        value: v,
        readOnly: true,
      }));
  }, [inspection]);

  if (isLoading || !inspection) {
    return (
      <AppShell>
        <div className="flex items-center justify-center p-12">
          <Loader2 size={20} className="animate-spin text-[var(--ink-3)]" />
        </div>
      </AppShell>
    );
  }

  // Tabs follow the record and the workflow, never state keys, so a tenant
  // running its own workflow gets the same tabs. Once a result is recorded
  // or the workflow ends, what was captured stays visible read-only.
  const closed = !!workflowInstance?.current_state?.is_terminal;
  // The safety check shows once a step waits on it or it was started.
  const showSafetyCheckTab =
    hasSafetyCheck &&
    (inspection.safety_check_due || safety?.status !== "not_started");
  const showReportTab = !!inspection.result;
  const showRepairTab =
    inspection.repair_tracking || !!inspection.repair_completed_at;

  return (
    <AppShell>
      <main className="page-container py-10">
        <div className="mx-auto flex max-w-5xl flex-col gap-6">
          <header className="flex flex-col gap-3">
            <button
              type="button"
              onClick={() => navigate("/inspections")}
              className="flex items-center gap-1.5 self-start text-xs text-[var(--ink-3)] hover:text-[var(--ink)]"
            >
              <ArrowLeft size={14} />
              Back to inspections
            </button>

            {/* Two-column header grid, mirroring the claims detail header:
              title/code on the left, action controls right-aligned and
              wrapping in the right cell. ``items-start`` so the tall
              display title top-aligns with the action block. */}
            <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-[1fr_1fr]">
              <div className="flex min-w-0 flex-col gap-1">
                {/* Named for the CLAIM that caused it - "Job Stop Claim
                  Inspection", not the generic "Quality Claim Inspection"
                  every claim-triggered inspection shares. An inspector reads
                  the title first, and it should already say what kind of job
                  this is. Falls back to the sub-type's own label for
                  inspections raised without a claim. */}
                <h1 className="font-display text-[42px] leading-[1.1] text-[var(--ink)]">
                  {inspection.claim_context?.claim_type_label
                    ? `${inspection.claim_context.claim_type_label} Claim Inspection`
                    : inspection.sub_type.label}
                </h1>
                <p className="flex flex-wrap items-center gap-x-2 font-mono text-[12px] text-[var(--ink-3)]">
                  <span>{inspection.code}</span>
                  {inspection.claim_uuid && inspection.claim_ref && (
                    <Button
                      variant="link"
                      onClick={() =>
                        navigate(`/claims/${inspection.claim_uuid}`)
                      }
                      className="h-auto p-0 font-mono text-[12px] text-[var(--brand)]"
                    >
                      {inspection.claim_ref}
                    </Button>
                  )}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2 md:justify-end">
                {/* Assignment pills + appointment confirmation - styled like
                  the claims header controls. Inspector (field tech) and
                  follow-up assignee are distinct people; Confirm opens a
                  dialog restating the scheduled date/time. */}
                <InspectionHeaderControls
                  inspection={inspection}
                  canEdit={canEditInspection}
                  closed={closed}
                />
                {/* Engine-driven action surface. WorkflowActionMenu reads
                  the inspection's visible workflow actions from
                  apps.workflows and drives every transition (schedule /
                  start / complete / flag-repair / confirm-repair /
                  cancel / SLA) through engine.transition. The bespoke
                  buttons/dialogs below remain for their richer capture
                  UX until a follow-up reconciles them onto the menu;
                  they no longer infer lifecycle (the engine owns it). */}
                {inspection.workflow_instance_uuid && (
                  <ResourceLookupContextProvider providers={resourceProviders}>
                    <WorkflowActionMenu
                      instanceUuid={inspection.workflow_instance_uuid}
                      workflowKey="default_inspection_workflow"
                      onActionExecuted={() => {
                        refetch();
                        // A result locks the checklist and the safety check;
                        // another visit starts them again.
                        void queryClient.invalidateQueries({
                          queryKey: ["inspection-checklist", uuid],
                        });
                        // Its effects can raise or close follow-up actions.
                        void queryClient.invalidateQueries({
                          queryKey: ["inspection-punch-list", uuid],
                        });
                        void queryClient.invalidateQueries({
                          queryKey: ["inspection-proof-links", uuid],
                        });
                      }}
                      onActionClick={(action) => {
                        // Reschedule reuses the same "Edit schedule" modal the
                        // pencil control opens - both edit scheduled_for, one
                        // source. Take over the click, open the modal in
                        // reason-required mode, and suppress the generic dialog.
                        if (
                          action.key === "reschedule" ||
                          action.key === "reschedule_from_confirmed"
                        ) {
                          setRescheduleAction(action.key);
                          setScheduleEditOpen(true);
                          return true;
                        }
                        // "Add evidence" is not a bare confirm - its real
                        // capture is the file-upload modal. That modal POSTs
                        // each file to the evidence endpoint, which creates
                        // the evidence row AND drives this add_evidence
                        // transition server-side (record_evidence_added). Open
                        // it here so the header button actually uploads
                        // evidence instead of recording an empty audit entry.
                        if (action.key === "add_evidence") {
                          setEvidenceOpen(true);
                          return true;
                        }
                      }}
                    />
                  </ResourceLookupContextProvider>
                )}
                {/* Legacy bespoke buttons removed - all actions (schedule,
                  start, complete, cancel) are driven by WorkflowActionMenu. */}
              </div>
            </div>

            <StatusBanner inspection={inspection} instance={workflowInstance} />
          </header>

          {/* Persistent notifications section - the current user's unread
          notifications for this inspection (new inspection awaiting scheduling,
          result recorded, open workflow issue). They stay visible and unread
          until explicitly marked read / dismissed, or until the standing
          condition clears itself. Read ones collapse into history. Driven by
          the notification infrastructure, mirroring the claim detail page. */}
          <NotificationsPanel
            unread={unreadNotifications}
            read={readNotifications}
            onMarkRead={markNotificationRead}
            onMarkAllRead={markAllNotificationsRead}
            onDismiss={dismissNotification}
            markAllPending={markNotificationsRead.isPending}
          />

          {/* Conditional tab structure per Ruby #389 LOA pattern:
              Overview        - always
              Safety check    - the type's safety check, once a step waits
                                on it or it was started (internal users)
              Checklist       - once the type names a checklist or one was
                                started (internal users only)
              Inspection Report - once a result is recorded (read-only: the
                                record_result action is the capture point)
              Repair Tracking - while the workflow tracks repairs, and after
              Evidence        - always
            State-specific action buttons live INSIDE their tab content so
            the header stays scoped to always-available actions (Cancel). */}
          <Tabs defaultValue="overview">
            {/* Scrolls sideways on a phone, where the tabs outrun the width. */}
            <TabsList variant="line" className="overflow-x-auto overflow-y-hidden">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              {showSafetyCheckTab && (
                <TabsTrigger value="safety">
                  {t("inspections.safety.tab", "Safety check")}
                </TabsTrigger>
              )}
              {showChecklistTab && (
                <TabsTrigger value="checklist">
                  {t("inspections.checklist.tab", "Checklist")}
                </TabsTrigger>
              )}
              {showReportTab && (
                <TabsTrigger value="report">Inspection Report</TabsTrigger>
              )}
              {showRepairTab && (
                <TabsTrigger value="repair">Repair Tracking</TabsTrigger>
              )}
              <TabsTrigger value="evidence">
                {t("inspections.evidenceTab")}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              <PinMyViewBar view={cardsView} />
              <DraggableCardGrid
                storageKey="inspection-detail-cards-v1"
                locked={cardsView.pinned}
                className="grid grid-cols-1 gap-4 lg:grid-cols-2"
              >
                <EditableDetailCard
                  key="scheduling"
                  title="Scheduling"
                  icon={<CalendarCheck size={16} />}
                  items={schedulingItems}
                  trailing={
                    <div className="flex items-center gap-2">
                      <Link
                        to={
                          inspection.scheduled_for
                            ? `/inspections/scheduling?date=${inspection.scheduled_for.slice(0, 10)}`
                            : "/inspections/scheduling"
                        }
                        className="text-[11px] text-[var(--ink-3)] underline-offset-2 hover:text-[var(--ink)] hover:underline"
                      >
                        Open Scheduling Console →
                      </Link>
                      {canEditInspection && (
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          onClick={() => setScheduleEditOpen(true)}
                          className="text-[var(--ink-3)] hover:text-[var(--ink)]"
                          aria-label="Edit schedule"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  }
                />
                <EditableDetailCard
                  key="linkage"
                  title="Linkage"
                  icon={<FileText size={16} />}
                  items={linkageItems}
                />
                {/* Named for who they are to the inspector, not for how the
                  platform models them: the person met on site is a home
                  owner, and the card is the address of the property. */}
                <EditableDetailCard
                  key="home-owner"
                  title="Home Owner"
                  icon={<User size={16} />}
                  items={consumerItems}
                />
                <EditableDetailCard
                  key="property"
                  title="Property Address"
                  icon={<MapPin size={16} />}
                  items={propertyItems}
                />
                {/* What the claim says is wrong, so the inspection team can
                  judge urgency without opening the claim. */}
                {claimContextItems.length > 0 && (
                  <EditableDetailCard
                    key="claim-context"
                    title="What to inspect"
                    icon={<ClipboardCheck size={16} />}
                    items={claimContextItems}
                  />
                )}
                {attributeItems.length > 0 && (
                  <EditableDetailCard
                    key="attributes"
                    title="Inspection attributes"
                    icon={<ClipboardCheck size={16} />}
                    items={attributeItems}
                  />
                )}
                <NotesPanel
                  key="notes"
                  data-card-title={t("notes.title", "Notes")}
                  recordId={inspection.uuid}
                  recordType="inspection"
                  canAddInternal
                  canAddCustomerVisible={false}
                  allowAttachments
                  // A note's files also land on the Evidence tab as Other
                  // (WPS-153); refetch so they show without a reload.
                  onNoteAdded={() => refetch()}
                />
                {/* What the inspection has sent, and Create New to send a
                  letter or email from it. The team's record: partners never
                  send from one. */}
                {!isPartnerAccess ? (
                  <RecordCommunicationsCard
                    key="communications"
                    data-card-title={t("claimComms.title", "Communications")}
                    recordType="inspection"
                    recordUuid={inspection.uuid}
                    testIdPrefix="inspection-comms"
                    emptyLabel={t(
                      "inspections.comms.empty",
                      "Nothing has been sent on this inspection yet.",
                    )}
                    letterHint={t(
                      "inspections.comms.letterHint",
                      "A formal letter, sent as a PDF",
                    )}
                  />
                ) : null}
                {showForms ? (
                  <DetailCard
                    key="forms"
                    title={t("forms.record.title", "Forms")}
                    icon={<ClipboardList size={16} />}
                    collapsible={false}
                    testId="inspection-forms-card"
                    trailing={
                      canEditInspection &&
                      can(SEND_REQUESTS) &&
                      inspectionForms.length > 0 ? (
                        <Button
                          variant="outline"
                          size="xs"
                          onClick={() => setSendingForm(true)}
                          data-testid="inspection-send-form"
                        >
                          <Send size={12} />
                          {t("forms.send.title", "Send a form")}
                        </Button>
                      ) : null
                    }
                  >
                    <RecordForms
                      recordType="inspection"
                      recordUuid={inspection.uuid}
                      inCard
                    />
                  </DetailCard>
                ) : null}
                {resultItems.length > 0 && (
                  <EditableDetailCard
                    key="result"
                    title={
                      inspection.outcome === "pass"
                        ? "Result - Pass"
                        : "Result - Issue"
                    }
                    icon={
                      inspection.outcome === "fail" ? (
                        <ShieldAlert size={16} />
                      ) : (
                        <CheckCircle2 size={16} />
                      )
                    }
                    items={resultItems}
                    trailing={
                      checklist?.locked ? (
                        <div className="flex items-center gap-3">
                          {/* Sharing emails a link, so it takes the
                            permission to send emails. */}
                          {can(SEND_PERMISSION_BY_KIND.notification) && (
                            <Button
                              variant="ghost"
                              size="xs"
                              onClick={() => setSharingReport(true)}
                              data-testid="inspection-report-share"
                              className="h-auto px-0 text-[11px] text-[var(--green)]"
                            >
                              <Share2 className="mr-1 h-3 w-3" />
                              {t("inspections.report.share", "Share")}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="xs"
                            disabled={downloadReport.isPending}
                            onClick={() =>
                              downloadReport.mutate(`${inspection.code}-report.pdf`)
                            }
                            data-testid="inspection-report-download"
                            className="h-auto px-0 text-[11px] text-[var(--green)]"
                          >
                            {downloadReport.isPending ? (
                              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                            ) : (
                              <Download className="mr-1 h-3 w-3" />
                            )}
                            {t("inspections.report.download", "Download report")}
                          </Button>
                        </div>
                      ) : undefined
                    }
                  />
                )}
                {cancelItems.length > 0 && (
                  <EditableDetailCard
                    key="cancellation"
                    title="Cancellation details"
                    icon={<XCircle size={16} />}
                    items={cancelItems}
                  />
                )}
              </DraggableCardGrid>
              {sharingReport && (
                <ShareReportDialog
                  inspectionUuid={inspection.uuid}
                  onClose={() => setSharingReport(false)}
                />
              )}
            </TabsContent>

            {showSafetyCheckTab && safety && (
              <TabsContent value="safety">
                <InspectionChecklistTab
                  inspectionUuid={inspection.uuid}
                  checklist={safety}
                  form="safety"
                />
              </TabsContent>
            )}

            {showChecklistTab && checklist && (
              <TabsContent value="checklist">
                <InspectionChecklistTab inspectionUuid={inspection.uuid} checklist={checklist} />
              </TabsContent>
            )}

            {showReportTab &&
              uuid && (
                <TabsContent value="report" className="flex flex-col gap-4">
                  <InspectionReportTab uuid={uuid} inspection={inspection} />
                </TabsContent>
              )}

            {showRepairTab &&
              uuid && (
                <TabsContent value="repair" className="flex flex-col gap-4">
                  <RepairTrackingPanel
                    inspectionUuid={uuid}
                    inspection={inspection}
                    canEdit={canEditInspection && !closed}
                  />
                </TabsContent>
              )}

            <TabsContent value="evidence">
              <section className="flex flex-col gap-4 rounded-lg border border-[var(--rule)] bg-[var(--bg-card)] p-5">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-sm font-medium text-[var(--ink)]">
                    Evidence
                  </h3>
                  {canEditInspection && (
                    <Button
                      size="sm"
                      onClick={() => setEvidenceOpen(true)}
                      className="min-h-[36px]"
                    >
                      <Camera size={14} className="mr-1.5" />
                      Add evidence
                    </Button>
                  )}
                </div>
                <EvidenceList items={inspection.evidence} />
                <p className="text-[11px] text-[var(--ink-3)]">
                  Upload photos, risk-assessment PDFs, Excel checklists or any
                  document captured on-site. On mobile, the picker opens the
                  camera directly.
                </p>
              </section>
            </TabsContent>
          </Tabs>

          {inspection.lifecycle_status === "draft" &&
            inspection.sub_type.requires_holding &&
            !inspection.holding_uuid && (
              <section className="rounded-lg border border-[var(--rule)] bg-[var(--bg-light)] p-4">
                <p className="text-xs text-[var(--ink-2)]">
                  {t(
                    "inspections.detail.registrationMissing",
                    "This inspection type needs a linked {{holdingNounLower}}, but none is attached. Re-create it from the New Inspection wizard with a {{holdingNounLower}} selected.",
                  )}
                </p>
              </section>
            )}

          {scheduleEditOpen && (
            <EditScheduleDialog
              open={scheduleEditOpen}
              onOpenChange={(o) => {
                setScheduleEditOpen(o);
                if (!o) setRescheduleAction(null);
              }}
              initialScheduledFor={inspection.scheduled_for}
              onSave={handleSaveSchedule}
              isPending={
                rescheduleAction
                  ? executeMutation.isPending
                  : updateMutation.isPending
              }
              reasonRequired={rescheduleAction !== null}
            />
          )}

          {evidenceOpen && (
            <AddEvidenceDialog
              onClose={() => setEvidenceOpen(false)}
              inspectionUuid={uuid}
            />
          )}

          {sendingForm && (
            <SendFormDialog
              recordType="inspection"
              recordUuid={inspection.uuid}
              onClose={() => setSendingForm(false)}
            />
          )}

        </div>
      </main>
    </AppShell>
  );
}
